import { useCallback } from 'react';

interface Attribute {
  name: string;
  value: unknown;
}

interface AttributeRule {
  displayType: string;
  type?: string;
  options?: string[];
  attributeName?: string;
  hasUnknownOption?: boolean;
}

interface NumberFieldProps {
  id: string;
  value: unknown;
  onChange: (value: unknown) => void;
  placeholder: string;
  min?: number;
  max?: number;
  integerOnly?: boolean;
}

interface InputConfig {
  type:
    | 'radio'
    | 'boolean-radio'
    | 'list'
    | 'number'
    | 'year'
    | 'month'
    | 'day'
    | 'text'
    | 'time'
    | 'null';
  isBoolean?: boolean;
  radioOptions?: string[];
  autocompleteOptions?: string[];
  numberFieldProps?: NumberFieldProps;
  attributeName?: string;
}

export const useAttributeInput = (
  attribute: Attribute,
  attributeRule: AttributeRule,
  onChange: (name: string, value: { value: unknown; isUnknown: boolean }) => void
) => {
  const handleChange = useCallback(
    (value: unknown) => {
      onChange(attribute.name, { value, isUnknown: false });
    },
    [attribute.name, onChange]
  );

  const getNumberFieldProps = useCallback(
    (fieldType: 'month' | 'day' | 'number'): Partial<NumberFieldProps> => {
      const baseProps: Partial<NumberFieldProps> = {
        id: `${attribute.name}-${fieldType}`,
        value: attribute.value,
        onChange: handleChange,
      };

      switch (fieldType) {
        case 'month':
          return {
            ...baseProps,
            placeholder: '1-12',
            min: 1,
            max: 12,
            integerOnly: true,
          } as Partial<NumberFieldProps>;
        case 'day':
          return {
            ...baseProps,
            placeholder: '1-31',
            min: 1,
            max: 31,
            integerOnly: true,
          } as Partial<NumberFieldProps>;
        case 'number':
          return {
            ...baseProps,
            placeholder: `Enter ${attribute.name.toLowerCase()}`,
          };
      }
    },
    [attribute.name, attribute.value, handleChange]
  );

  const getRadioOptions = useCallback(() => {
    return [...new Set(attributeRule.options)];
  }, [attributeRule.options]);

  const getAutocompleteOptions = useCallback(() => {
    return [...new Set(attributeRule.options || [])];
  }, [attributeRule.options]);

  const getInputConfig = useCallback((): InputConfig => {
    const displayType = attributeRule.displayType;
    const attributeType = attributeRule.type;

    switch (displayType) {
      case 'radio':
        if (attributeType === 'boolean') {
          return { type: 'boolean-radio', isBoolean: true };
        }
        return { type: 'radio', radioOptions: getRadioOptions() };

      case 'list':
        return {
          type: 'list',
          autocompleteOptions: getAutocompleteOptions(),
          attributeName: attributeRule.attributeName,
        };

      case 'number':
        return {
          type: 'number',
          numberFieldProps: getNumberFieldProps('number') as NumberFieldProps,
        };

      case 'year':
        return { type: 'year' };

      case 'month':
        return {
          type: 'month',
          numberFieldProps: getNumberFieldProps('month') as NumberFieldProps,
        };

      case 'day':
        return {
          type: 'day',
          numberFieldProps: getNumberFieldProps('day') as NumberFieldProps,
        };

      case 'text':
        return { type: 'text' };

      case 'time':
        return { type: 'time' };

      default:
        return { type: 'null' };
    }
  }, [
    attributeRule.displayType,
    attributeRule.type,
    attributeRule.attributeName,
    getNumberFieldProps,
    getRadioOptions,
    getAutocompleteOptions,
  ]);

  return {
    handleChange,
    getInputConfig,
    getNumberFieldProps,
    getRadioOptions,
    getAutocompleteOptions,
    attributeType: attributeRule.type as string | undefined,
  };
};
